import React, { useRef, useState } from 'react';
import { Play, Pause, Volume2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { commsApi } from '@/utils/commsApi';
import { formatDuration } from '@/utils/commsFormat';

const Transcript = ({ status, text }) => {
  if (status === 'pending') {
    return <div className="voice-transcript pending" data-testid="voice-transcript"><Loader2 size={11} className="spin" /> Transcribing…</div>;
  }
  if (status === 'done' && text) {
    return <div className="voice-transcript" data-testid="voice-transcript">{text}</div>;
  }
  if (status === 'failed') {
    return <div className="voice-transcript unavailable" data-testid="voice-transcript">Transcript unavailable</div>;
  }
  return null;
};

export const VoiceClip = ({ fileId, duration, live, transcript, transcriptStatus, onPlayed }) => {
  const audioRef = useRef(null);
  const reportedRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);

  const handlePlay = () => {
    setPlaying(true);
    if (!reportedRef.current) {
      reportedRef.current = true;
      onPlayed?.();
    }
  };

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => toast.error('Could not play this voice message'));
    else audio.pause();
  };

  const total = duration || audioRef.current?.duration || 0;
  const pct = total ? Math.min(100, (position / total) * 100) : 0;

  return (
    <div className={`voice-clip ${live ? 'live' : ''}`} data-testid="voice-clip">
      <div className="voice-player">
        <audio
          ref={audioRef}
          src={commsApi.fileUrl(fileId)}
          preload="none"
          onPlay={handlePlay}
          onPause={() => setPlaying(false)}
          onEnded={() => { setPlaying(false); setPosition(0); }}
          onTimeUpdate={(e) => setPosition(e.target.currentTime)}
        />
        <button type="button" className="voice-play" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'} data-testid="voice-play-button">
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <div className="voice-bar"><span style={{ width: `${pct}%` }} /></div>
        <span className="voice-duration">{live ? 'Playing…' : formatDuration(playing ? position : total)}</span>
        {live && <Volume2 size={14} className="pulse" />}
      </div>
      <Transcript status={transcriptStatus} text={transcript} />
    </div>
  );
};
